import { API_CONFIG } from "../../config";
import { apiClient } from "../config";
import { ListingCore } from "../../types"; // Import ListingCore

interface ApiResponse<T> {
  status: string;
  data: T;
}

/**
 * Fetches listings for the current logged in user
 */
export const getUserListings = async (status?: string) => {
  try {
    const url = status
      ? `${API_CONFIG.ENDPOINTS.LISTINGS}/user?status=${status}`
      : `${API_CONFIG.ENDPOINTS.LISTINGS}/user`;

    const response = await apiClient.get<
      ApiResponse<ListingCore[]> & { meta: { totalItems: number } }
    >(url);

    if (!response.data) {
      return { listings: [], totalListings: 0 };
    }

    return {
      listings: response.data.data || [],
      totalListings: response.data.meta?.totalItems || 0,
    };
  } catch (error) {
    console.error("Error fetching user listings:", error);
    // Re-throw the error so react-query can handle it with its onError callback
    throw error;
  }
};

/**
 * Updates a user's listing by ID
 */
export const updateUserListing = async (
  id: number,
  data: Partial<ListingCore>
) => {
  try {
    const response = await apiClient.put<ApiResponse<{}>>(
      `${API_CONFIG.ENDPOINTS.LISTINGS}/${id}`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Publishes a user's listing by ID
 */
export const publishUserListing = async (id: number) => {
  try {
    const response = await apiClient.put<ApiResponse<{}>>(
      `${API_CONFIG.ENDPOINTS.LISTINGS}/${id}/publish`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Deletes a user's listing by ID
 */
export const deleteUserListing = async (id: number) => {
  try {
    const response = await apiClient.delete<ApiResponse<{}>>(
      `${API_CONFIG.ENDPOINTS.LISTINGS}/${id}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches a user's listing by ID
 */
export const getUserListingById = async (id: string) => {
  try {
    const response = await apiClient.get<ApiResponse<{ listing: ListingCore }>>(
      `${API_CONFIG.ENDPOINTS.LISTINGS}/user/${id}`
    );

    if (!response.data) {
      throw new Error("Invalid listing data format");
    }

    return response.data.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Toggles the featured status of a user's listing by ID
 */
export const toggleListingFeatured = async (id: number) => {
  try {
    const response = await apiClient.put<ApiResponse<{}>>(
      `${API_CONFIG.ENDPOINTS.LISTINGS}/${id}/toggle-featured`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};
